/**
 * Phase 6 Component 27 — CSV serializer for report rows.
 *
 * Pure module that converts the pure-data-fn output into a CSV string suitable
 * for download or storage. RFC 4180 quoting; honours the report's column
 * spec (header order, type-aware formatting).
 *
 * PHI redaction: when `redactPhi` is true, columns flagged `isPhi: true` in
 * the spec render as the string '[REDACTED]'. Used for scheduled reports
 * sent via email.
 */

import type { ColumnSpec } from './framework';

export interface SerializeOptions {
  redactPhi?: boolean;
  /**
   * Coverage notices (for example, paid figures pending) written as leading
   * rows, then a blank line, before the column header, so the file never
   * reads as complete on its own.
   */
  notices?: string[];
}

const FORMULA_LEAD = /^[=+\-@\t\r]/;
// A plain decimal literal (optionally negative / exponent). Only values the
// serializer itself produced from a numeric column AND that match this shape
// skip the formula guard, so `-12.50` stays a number in Excel while a string
// smuggled into a numeric column (`=1+1`, `-cmd`) is still neutralised.
const NUMERIC_LITERAL = /^-?\d+(\.\d+)?([eE][+-]?\d+)?$/;
const NUMERIC_TYPES: ReadonlySet<ColumnSpec['type']> = new Set([
  'number',
  'currency',
  'duration_minutes',
]);

function csvEscape(value: unknown, type: ColumnSpec['type'] = 'string'): string {
  if (value === null || value === undefined) return '';
  const raw = String(value);
  const isSafeNumeric = NUMERIC_TYPES.has(type) && NUMERIC_LITERAL.test(raw);
  const s = !isSafeNumeric && FORMULA_LEAD.test(raw) ? `'${raw}` : raw;
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function formatCellValue(value: unknown, type: ColumnSpec['type']): string {
  if (value === null || value === undefined) return '';
  switch (type) {
    case 'currency':
      // ISO formatting: 2 decimals, no symbol (consumers add their own)
      return Number(value).toFixed(2);
    case 'number':
      return String(value);
    case 'boolean':
      return value ? 'true' : 'false';
    case 'date':
    case 'datetime':
      return String(value);
    case 'duration_minutes':
      return String(Math.round(Number(value)));
    default:
      return String(value);
  }
}

export function serializeReportToCsv(
  columns: ColumnSpec[],
  rows: Record<string, unknown>[],
  opts: SerializeOptions = {},
): string {
  const redact = opts.redactPhi ?? false;

  const header = columns.map((c) => csvEscape(c.label)).join(',');
  const body = rows
    .map((row) =>
      columns
        .map((col) => {
          if (redact && col.isPhi) return csvEscape('[REDACTED]');
          const raw = row[col.key];
          return csvEscape(formatCellValue(raw, col.type), col.type);
        })
        .join(','),
    )
    .join('\r\n');

  const preface = (opts.notices ?? [])
    .map((notice) => `${csvEscape(`Notice: ${notice}`)}\r\n`)
    .join('');
  const lead = preface ? `${preface}\r\n` : '';
  return body.length > 0 ? `${lead}${header}\r\n${body}\r\n` : `${lead}${header}\r\n`;
}
